/* eslint-disable no-param-reassign */
import { observable } from 'mobx';
import { GraphType } from '@src/slide/infrastructure/module/dependent/shape-info/constant';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import {
    AttachmentElement,
    AttachmentInfo,
    AttachmentRect,
    MetaInfo,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import AttachmentUploader from '@src/js/common/ark-extensions/dc-components/attachment-uploader';
import {
    EStatusEvents,
    IStatusChangeOptions,
} from '@src/js/common/ark-extensions/dc-components/attachment-uploader/type';
import { transaction } from '@src/slide/domain/slide-app';
import get from 'lodash/get';
import {
    BaseModelSdkTypeV3,
    IBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/base-model.interface';
import { isIntersect } from '@utils/attachment';
import {
    AUDIO_DEFAULT_IMG,
    AttachmentClassify,
    DEFAULT_AUDIO_HEIGHT,
    DEFAULT_AUDIO_WIDTH,
    DEFAULT_CARD_HEIGHT,
    DEFAULT_CARD_WIDTH,
    DEFAULT_IMAGE_HEIGHT,
    DEFAULT_IMAGE_WIDTH,
    DEFAULT_TEXT_HEIGHT,
    DEFAULT_TEXT_WIDTH,
    DEFAULT_VIDEO_HEIGHT,
    DEFAULT_VIDEO_WIDTH,
    EShowType,
    TEXT_LINK_DEFAULT_COLOR,
    TEXT_LINK_DEFAULT_FONT_FAMILY,
    TEXT_LINK_DEFAULT_FONT_SIZE,
    TRANSPARENT_PIXEL_PNG,
    VIDEO_DEFAULT_IMG,
} from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import { Locks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import MediaUtils from '@editor/src/page/index/components/canvas/element-types/image-element/media-container/media-utils';
import type { IAttachmentClassify, IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { IAttachmentManager } from './attachment-manager.interface';
import { IShapeModel, ShapeSubType } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { blobToBase64, imageUrlToBlob } from '@src/slide/infrastructure/module/dependent/image/upload';
import { CDNImage, uploadImageBlob } from '@src/slide/infrastructure/module/dependent/image/image-toolkit/cgi-upload';
import ENV from '@src/slide/infrastructure/module/dependent/runtime-environment/enviroment';
import { fetchImageSize, getDefaultImageSizeByRatio, getDefaultVideoSizeByRatio } from '@src/slide/domain/ui-model/element-modeles/property/attachment/utils/size';
import { DEFAULT_PADDING_TB, DEFAULT_PADDING_LR } from '@src/slide/domain/ui-model/element-modeles/property/text/constants';
import { DataType, K_DEFAULT_TRIGGER_ID, AnimType, ResourceType } from '@tencent/slide-sdk-model';
import { TextConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-converter';
import { IParagraph } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-para.interface';
import { SlideDataServices, onSlideAppReady } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import {
    ISlideRenderStateManager, IStatePointEnum,
} from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';
import { replaceMediaUrl } from '@com-pc/paging-selection/utils';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import axios from 'axios';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';
import {
    isAttachmentModel,
    isAudioElement,
    isShapeModel, isUiModelIsAttachment,
    isVideoElement,
} from '@src/slide/domain/ui-model/elements/element-judge';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import { TSnackbarShowConfig  } from '@tencent/dui/lib/components/Snackbar/interface';
import { isVip } from '@src/js/common/ark-extensions/dc-components/vip-linktext';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { RenderPostionType } from '@src/js/constant';
const DEFAULT_POS = 100;

const SPACE = 50; // 间距
const NO_MEDIA_DURATION = '0';
export const TEMPLINK = 'temp_fake_link';
const UPLOAD_VIDEO_FILE_POSTER_IMAGE_TYPE = 'jpeg';
export const getUploadingMediaAttachmentId = (model: IPictureModel) => {
    const regex = new RegExp(`^${TEMPLINK}(.*)`);
    return model?.video?.link?.match(regex)?.[1] || model?.audio?.link?.match(regex)?.[1];
};

export enum TranscodeType {
    MP3 = 0, // mp3
    MP4ORIGINAL = 1, // 原画转码
    MP4FLU = 2, // 480p分辨率转码
    MP4HD = 3, // 720p分辨率转码
    MP4FHD = 4, // 1080p分辨率转码
}

interface IAttachmentManagerProps {
    selectionService: ISelectionService;
    uiDataService: IUiDataService;
    slideRenderStateManager: ISlideRenderStateManager;
}

/**
 * 附件管理。包含上传过程的一个状态管理以及上传完成之后的状态管理
 */
export default class AttachmentManager implements IAttachmentManager {
    public uploadingMap: Map<string, AttachmentElement>; // 存储所有本次上传中（未上传完成都算上传中）的附件信息
    public errorMap: Map<string, AttachmentElement> = new Map();// 记录所有上传错误的附件信息
    public modelIdToLocalAttachmentMap: Map<string, any> = new Map();
    /**
     * 附件下载成功值++
     */
    @observable
    public attachmentSuccessHappen = 1;
    @observable
    public attachmentErrorHappen = 1;
    private uploader: AttachmentUploader;
    private hasRegisterPresentation: boolean;
    private registerSlideMap = new WeakMap();
    private selectionService: ISelectionService;
    private uiDataService: IUiDataService;
    private slideRenderStateManager: ISlideRenderStateManager;
    private snackbar: any;

    public constructor(props: IAttachmentManagerProps) {
        const { selectionService, uiDataService, slideRenderStateManager } = props;
        this.selectionService = selectionService;
        this.uiDataService = uiDataService;
        this.slideRenderStateManager = slideRenderStateManager;
        const uploader: AttachmentUploader = AttachmentUploader.getInstance();
        // 注册选择文件之后的监听
        uploader.addUploadListener(this);
        this.uploader = uploader;
        this.uploadingMap = new Map<string, AttachmentElement>();
        onSlideAppReady(async () => {
            await this.slideRenderStateManager
                .getStatePoints()[IStatePointEnum.visibleSlideRender].getPointCompletePromise();
            uploader.initUploader();
        });
    }

    /* eslint-disable complexity */
    public onStatusChanges(opt: IStatusChangeOptions): void {
        if (opt?.statusName === EStatusEvents.SUCCESS) {  // 处理上传成功
            const attachmentId = opt.options?.id;
            const attachElement: AttachmentElement | undefined = this.uploadingMap.get(opt.options?.id)
                || this.errorMap.get(opt.options.id);
            const objKey = opt.options?.objKey;
            if (objKey && attachElement) {
                this.uploadingMap.delete(attachmentId);  // 删除完成后，删除存储
                this.errorMap.delete(attachmentId);
                const model: IBaseModel | null = this.uiDataService.presentation.
                    findElementWithId(attachElement.slideId, attachElement.shapeId);
                window.log?.info('onStatusChanges success : delete id=', attachmentId, !!model);
                if (model) {
                    this.updateModelIdToLocalAttachment(
                        attachElement.shapeId,
                        { status: EStatusEvents.SUCCESS, objKey },
                    );
                    const { attachment } = attachElement;
                    attachment.objKey = objKey;
                    this.handleAttachmentElement(model, attachElement);
                    this.attachmentSuccessHappen += 1; // 触发状态更新
                }
            } else {
                window.log?.warn(`handle upload success error: objKy = ${objKey}, attachment =`, attachElement);
            }
        } if (opt?.statusName === EStatusEvents.FILEREAD) {
            if (Array.isArray(opt.options)) {
                // 手动给 fileread 阶段的 attachmentInfo 添加 status 参数
                opt.options.forEach(item => item.status = EStatusEvents.FILEREAD);
                this.addAttachmentInSlide(opt.options);
            }
        } else if (opt?.statusName === EStatusEvents.ERROR) {
            const attachmentId = opt.options?.id;
            const attachElement: AttachmentElement | undefined = this.uploadingMap.get(opt.options?.id);
            if (attachElement) {
                this.uploadingMap.delete(attachmentId);  // 删除完成后，删除存储
                window.log?.info('onStatusChanges error : id=', attachmentId);

                this.updateModelIdToLocalAttachment(attachElement.shapeId, { status: EStatusEvents.ERROR });

                this.errorMap.set(attachmentId, attachElement);

                this.attachmentErrorHappen += 1; // 触发状态更新

                const model: IBaseModel | null = this.uiDataService.presentation.
                    findElementWithId(attachElement.slideId, attachElement.shapeId);
                this.snackbar?.close();
                if (model) {
                    !ENV.IS_PC_LOCAL_EDIT
                    && this.showSnackbar({ message: i18n.t('视频上传失败, 请重试'), duration: 3000, type: 'error' });
                }
            }
        }
    }
    /* ------------------上传回调 end---------------------------------*/

    /**
     * 直接插入附件
     */
    public insertAttachmentDirectly(attachments: AttachmentInfo[]): void {
        const curSlide = this.selectionService.currentSlide;

        if (!curSlide) return;

        const { initLeft, initTop } = this.findAttachmentInitPos();

        attachments.forEach((attachment: AttachmentInfo, index) => {
            // 0. 初始化 attachment 和其他品类的名称显示格式保持一致
            attachment.name = `${attachment.name || ''}${attachment.name ? '.' : ''}${attachment.filetype || ''}`;
            attachment.type = attachment.filetype;
            attachment.size = Number.isNaN(Number(attachment.size)) ? undefined : Number(attachment.size);

            // 1. 添加presentation和slide的changeSet监听
            this.addElementDeleteObserver(curSlide);

            // 2. 创建attachElement
            const attachElement: AttachmentElement = { attachment, slideId: curSlide.id as string, shapeId: '' };

            // 3. 添加元素
            const props = this.getAttachmentProps(attachment, index, initLeft, initTop);
            const extraInfo = this.getAttachmentExtraInfo(props.attachment);
            curSlide.addElement([UIModelType.SHAPE], [props], [extraInfo]);

            // 4. 添加完之后，更新shapeId
            const element = curSlide.children[curSlide.children.length - 1];
            attachElement.shapeId = element.id as string;
            attachElement.shapeUniqueId = (element.sdkData as BaseModelSdkTypeV3)?.id;

            // 5. 获取刚刚添加的shape
            const shapeModel: IBaseModel | null = this.uiDataService.presentation.
                findElementWithId(attachElement.slideId, attachElement.shapeId);

            if (!shapeModel) return;

            // 6. 将attachElement添加到shape中
            this.handleAttachmentElement(shapeModel, attachElement);
        });
    }

    public deleteAttachment(info: AttachmentInfo): void {
        if (info.id) {
            this.uploader.removeAttachment(info.id);
        }
    }

    public isUploadingAttachment(id: string): boolean {
        return !!id && this.uploadingMap.has(id);
    }

    /**
     * 判断 element model 是否处于上传状态
     */
    public isVideoElementUploading(model: IPictureModel) {
        if (isVideoElement(model)) {
            const uploadingAttachmentId = getUploadingMediaAttachmentId(model);
            if (uploadingAttachmentId) {
                return this.isUploadingAttachment(uploadingAttachmentId);
            }
            return false;
        }
        return false;
    }


    /**
     * 生成上传时放置的临时元素的部分属性
     */
    public generateUploadingAttachmentShowType(classify?: IAttachmentClassify): MetaInfo {
        const metaInfo: MetaInfo = {
            showType: EShowType.TEXT,
            width: 0,
            height: 0,
        };

        switch (classify) {
            case AttachmentClassify.VIDEO:
                metaInfo.showType = EShowType.PREVIEW;
                metaInfo.width = DEFAULT_VIDEO_WIDTH;
                metaInfo.height = DEFAULT_VIDEO_HEIGHT;
                break;
            case AttachmentClassify.IMAGE:
                metaInfo.showType = EShowType.PREVIEW;
                metaInfo.width = DEFAULT_IMAGE_WIDTH;
                metaInfo.height = DEFAULT_IMAGE_HEIGHT;
                break;
            case AttachmentClassify.DOC:
                metaInfo.showType = EShowType.CARD;
                metaInfo.width = DEFAULT_CARD_WIDTH;
                metaInfo.height = DEFAULT_CARD_HEIGHT;
                break;
            case AttachmentClassify.AUDIO:
                metaInfo.showType = EShowType.TEXT;
                break;
        }

        return metaInfo;
    }

    /**
     * 生成attachment文本信息，用来进行占位
     * @param attachment
     */
    public getAttachmentExtraInfo(attachment: AttachmentInfo) {
        const fontFamily = TEXT_LINK_DEFAULT_FONT_FAMILY;
        const runStyles = {
            fontSize: TEXT_LINK_DEFAULT_FONT_SIZE,
            fontFamily,
            fonts: { ea: fontFamily, latin: fontFamily },
            color: TEXT_LINK_DEFAULT_COLOR,
            tdColor: new TDColor({
                rgbaStr: TEXT_LINK_DEFAULT_COLOR,
            }),
        };
        const textBodyStyle = attachment.showType === EShowType.TEXT || attachment.showType === EShowType.SIMPLE_TEXT
            ? {
                autoFitType: AutoFitType.SHAPE_AUTO_FIT,
                bodyStyle: { whiteSpace: 'pre', ...this.getAttachmentShapePadding(attachment.showType) }, // 不换行
            } : {};
        const paragraphList = [
            {
                paragraphProps: { lineHeight: 1.56, lineHeightIs: 'percent' },
                textRuns: [
                    {
                        text: attachment.name,
                        type: 'run',
                        styles: runStyles,
                    },
                ],
            },
        ];
        // 后续在slide-base-model的addElement时会appendUiDataInheritProp,为了保存自定义数据，将所有数据加上raw-
        TextConverter.addParagraphListRawSignProps(paragraphList as IParagraph[], paragraphList as IParagraph[]);
        return {
            textBodyStyle,
            paragraphList,
        };
    }

    /**
     * 获取附件占位形状的 padding 值
     * @param showType
     * @returns
     */
    public getAttachmentShapePadding(showType: EShowType) {
        if (showType === EShowType.TEXT) {
            return {
                // 需要让出 icon 的空间
                paddingLeft: 32,
                paddingRight: DEFAULT_PADDING_LR,
                paddingTop: DEFAULT_PADDING_TB,
                paddingBottom: DEFAULT_PADDING_TB,
            };
        }
        return {};
    }

    public updateModelIdToLocalAttachment(modelId: string, props: any) {
        const attachment = this.modelIdToLocalAttachmentMap.get(modelId);
        if (attachment) {
            Object.keys(props).forEach((key) => {
                attachment[key] = props[key];
            });
        } else {
            this.modelIdToLocalAttachmentMap.set(modelId, props);
        }
    }

    public getLocalAttachment(modelId: string) {
        return this.modelIdToLocalAttachmentMap.get(modelId);
    }

    // 某些情况下的 localAttachment 参数直接传递给 Attachment 组件会有问题，这里加一层拦截
    public getAvailableLocalAttachment(modelId: string) {
        const localAttachment = this.getLocalAttachment(modelId);
        return (localAttachment?.name && localAttachment?.type) ? localAttachment : null;
    }

    public getUploadStatus(modelId: string): EStatusEvents {
        return this.modelIdToLocalAttachmentMap.get(modelId)?.status;
    }

    public isUploadError(modelId: string): boolean {
        return this.getUploadStatus(modelId) === EStatusEvents.ERROR;
    }

    /**
     * 从 attachment-manager 的角度来判断元素是否为幽灵附件元素(该类元素不应该参与文档的渲染)
     * 幽灵元素产生原因：附件上传过程中如果用户刷新了页面，处于上传状态的 attachment 元素无法被正常销毁，此时变为了幽灵元素
     * @param model
     * @returns boolean，非 attachment model 返回 false
     */
    public isGhostAttachment(model: IElementBaseModel, region = '') {
        if (!isUiModelIsAttachment(model)) return false;

        if (isAudioElement(model)) {
            // 预览态的音频默认都显示
            return false;
        }
        /**
         * 线上视频元素显示条件
         * 1. 有本地文件，此时 model?.video?.link 为 TEMPLINK 开头的字符串，播放的视频也是用的本地文件
         * 2. 或者，无本地文件，model?.video?.link 不为 TEMPLINK 开头，表明本地文件已经落盘，可以显示视频元素
         * tip：在有本地文件的情况下，即使视频文件已经落盘完成，model?.video?.link 依然为 TEMPLINK 开头的字符串.
         */
        if (isVideoElement(model)) {
            const videoAttachmentId = getUploadingMediaAttachmentId(model);
            const loadingMap = this.uploadingMap;
            const localAttachment = this.modelIdToLocalAttachmentMap.get(model.id as string);
            if (videoAttachmentId) {
                const shouldShow = !!loadingMap?.has(videoAttachmentId) || localAttachment
                    || this?.getUploadStatus(model?.id as string) === EStatusEvents.ERROR;
                (!shouldShow && model.selected) && model.selectionService?.selectedElements.remove(model);
                return !shouldShow;
            }
            return false;
        }

        /**
         * 非缩略图区域的附件不展示
         * 正常来说移动端编辑页和演示态的缩略图应该显示 canvas 的附件元素，但是因为移动端列表页和前两个区域复用了
         * 相同的 application 和截图，而且移动端列表页要展示 dom 版本的附件，所以就整体都不显示 canvas 附件元素了
         */
        if (region
            && region !== RenderPostionType.LEFTLIST
            && region !== RenderPostionType.PRESENTER_VIEW
        ) {
            return true;
        }

        // 是附件但【不在上传阶段、且上传未完成的(无objKey)】的不显示
        // 支持秒播的本地附件的上传失败状态需要显示
        const hasNoKey = !(model as IPictureModel)?.props?.attachment?.objKey;
        const loadingMap = this?.uploadingMap;
        const isLoading = loadingMap?.has((model as IPictureModel)?.props?.attachment?.id);
        if (hasNoKey && !isLoading) {
            const localAttachment = this.getLocalAttachment(model?.id as string);
            return localAttachment?.status !== EStatusEvents.ERROR;
        }

        return false;
    }

    /**
     * 处理附件元素
     * @param model attachElement
     * @returns
     */
    private handleAttachmentElement(model: IBaseModel | IPictureModel,  attachElement: AttachmentElement): void {
        const { attachment } = attachElement;
        const { classify } = attachment;
        if (classify === AttachmentClassify.VIDEO || classify === AttachmentClassify.AUDIO) {
            if (isVideoElement(model)) {
                this.handleVideoSuccess(model, attachment);
            } else {
                this.handleMediaSuccess(model, attachment);
            }
        } else if (classify === AttachmentClassify.IMAGE) {
            this.handleImageSuccess(model, attachment);
        } else if (classify === AttachmentClassify.DOC) {
            this.handleDocSuccess(model, attachment);
        } else {
            this.handleAttachmentSuccess(model, attachment);
        }
    }

    /**
     * 获取附件的缩略图并且重新保存到 cdn 然后落盘
     * 因为附件的这些地址有有效期，会导致导出的时候链接失效
     * @param attachment
     * @returns
     */
    private async uploadAndGetMediaPosterUrl(attachment: AttachmentInfo): Promise<CDNImage | null> {
        const { dcAttachmentModule } = AttachmentUploader.getInstance();
        try {
            const posterUrl = await dcAttachmentModule.getMediaPoster({
                objKey: attachment.objKey!,
                // TODO 本地编辑没有padId导致组件无法渲染预览图(cPadId)
                fileId: SlideDataServices.DocInfoManager()?.padId ?? '',
            });
            const blob = await imageUrlToBlob(posterUrl) as string;
            return await uploadImageBlob({ blob: new Blob([blob]) });
        } catch (error) {
            // 可忽略错误，不弹出错误提示
            return null;
        }
    }

    /**
     * 获取图片附件封面图地址和宽高
     * 这个封面图地址是个透明图片，会落盘在图片附件 src 数据中，有以下作用：
     * 1. 保存宽高，用于后续计算图片裁剪属性，避免图片被拉伸
     * 2. 和正常图片逻辑保持一致，避免导出时单独处理 src 为空的情况
     * slide 中渲染图片附件没有使用这个地址，是直接从 dc 中获取的封面图地址进行渲染的
     * @param attachment
     * @returns
     */
    private async getImageAttachmentPoster(attachment: AttachmentInfo): Promise<null | Pick<CDNImage, 'width' | 'height' | 'url'>> {
        const { dcAttachmentModule } = AttachmentUploader.getInstance();
        try {
            const objKey = attachment.objKey!;
            // TODO 本地编辑没有padId导致组件无法渲染预览图(cPadId)
            const fileId = SlideDataServices.DocInfoManager()?.padId ?? '';
            const posterUrl = await dcAttachmentModule.getMediaPoster({
                objKey,
                fileId,
            });
            let width: number;
            let height: number;
            try {
                ({ width, height } = await fetchImageSize(posterUrl));
            } catch (error) {
                width = 1;
                height = 1;
            }
            // 图片地址中添加 objKey 和 fileId 是为了保证地址是唯一的，后台导出时依赖这个唯一性用于关联附件和图片元素
            return {
                width, height, url: `${TRANSPARENT_PIXEL_PNG}?w=${width}&h=${height}&objKey=${attachment.objKey}&fileId=${fileId}`,
            };
        } catch (error) {
            return null;
        }
    }

    /**
     * 添加附件元素到当前slide
     * 附件一开始默认为 SHAPE ELEMENT（进行占位），上传完成后才能进行操作（如播放），同时上传完成后会删除 SHAPE ，替换为 PIC（或者其他）
     * 视频附件（DC组件）实现了边上传边播放，开始上传时直接就可以播放本地文件,
     * 因此直接添加 PIC ELEMENT
     * @param attachments
     */
    private addAttachmentInSlide(attachments: AttachmentInfo[]) {
        const curSlide = this.selectionService.currentSlide;
        if (curSlide) {
            const { initLeft, initTop } = this.findAttachmentInitPos();
            attachments.forEach((attachment: AttachmentInfo, index) => {
                if (attachment.id && this.uploadingMap.has(attachment.id)) return;
                window.log?.info('addAttachmentInSlide =', attachment.name);
                // 1. 添加presentation和slide的changeSet监听
                this.addElementDeleteObserver(curSlide);

                // 2. 保存到上传列表
                const attachElement: AttachmentElement = { attachment, slideId: curSlide.id as string, shapeId: '' };
                this.uploadingMap.set(attachment.id!, attachElement);

                // 3. 添加元素
                if (attachment.classify ===  AttachmentClassify.VIDEO) {
                    this.addVideoAttachmentElement(attachment, attachElement, index, initLeft, initTop);
                    // 上报插入视频
                    this.reportVideoAttachment(TDWActionType.INSERT_VIDEO, attachElement.attachment.type);
                } else {
                    this.addNormalAttachmentElement(attachment, attachElement, index, initLeft, initTop);
                }
            });
        }
    }

    /**
     * 添加(除线上视频)附件元素
     */
    private addNormalAttachmentElement(
        attachment: any,
        attachElement: AttachmentElement,
        index: number,
        initLeft: number,
        initTop: number,
    ) {
        const curSlide = this.selectionService.currentSlide;
        if (curSlide) {
            const props = this.getAttachmentProps(attachment, index, initLeft, initTop);
            const extraInfo = this.getAttachmentExtraInfo(props.attachment);
            curSlide.addElement([UIModelType.SHAPE], [props], [extraInfo]);

            // 4. 添加完之后，更新shapeId
            const element = curSlide.children[curSlide.children.length - 1];
            attachElement.shapeId = element.id as string;
            attachElement.shapeUniqueId = (element.sdkData as BaseModelSdkTypeV3)?.id;
        }
    }

    private addElementDeleteObserver(slideModel: IBaseModel) {
        if (slideModel.sdkData && !this.registerSlideMap.has(slideModel.sdkData)) {
            (slideModel.sdkData as BaseModelSdkTypeV3)?.subscribe((change) => {
                this.elementDeleteCallback(slideModel.sdkData, change);
            });
            this.registerSlideMap.set(slideModel.sdkData, true);
        }
        if (!this.hasRegisterPresentation) {
            this.hasRegisterPresentation = true;
            // 注册删除slide，导致元素被删除的监听
            (this.uiDataService.presentation?.sdkData as BaseModelSdkTypeV3)?.subscribe((change) => {
                this.elementDeleteCallback(this.uiDataService.presentation?.sdkData, change);
            });
        }
    }

    /**
     * 本页元素被删除后的监听
     * @param sdkData
     * @param changeSet
     */
    private elementDeleteCallback = (sdkData, changeSet) => {
        if (sdkData.type === DataType.Slide) {
            this.uploadingMap.forEach((attachElement: AttachmentElement) => {
                if (attachElement.slideId !== sdkData.id) return;
                const uploadId = attachElement.attachment.id;
                if (attachElement.shapeUniqueId) {
                    if (!sdkData.shapeList?.some(e => e.id === attachElement.shapeUniqueId)) {
                        window.log?.info?.('abortAttachment=', uploadId);
                        this.uploader.abortUpload(uploadId!);
                    }
                }
            });
            return;
        }
        // 是presentation上的slide有变更
        this.uiDataService.presentation?.children?.forEach((slide) => {
            if (this.isSlideHasElementUploading(slide)) {  // 如果是删除然后re do操作，需要重新挂载observer
                this.addElementDeleteObserver(slide);
            }
        });
    };

    /**
     * 检查slide中，是否有附件正在上传
     * @param slideModel
     */
    private isSlideHasElementUploading(slideModel: IBaseModel): boolean {
        let hasElementUploading = false;
        this.uploadingMap.forEach((value: AttachmentElement) => {
            if (value.slideId === slideModel.id) {
                hasElementUploading = true;
            }
        });
        return hasElementUploading;
    }

    /**
     * 生成attachment props数据
     * @param attachment
     * @param index
     * @param initLeft
     * @param initTop
     */
    private getAttachmentProps(attachment: AttachmentInfo, index: number, initLeft: number, initTop: number) {
        const { top, left } = this.getFramePosition(index, initLeft, initTop);
        const { showType, width, height } = this.generateUploadingAttachmentShowType(attachment.classify);
        let style: Record<string, any> = {
            bgShape: GraphType.RECT,
            position: 'absolute',
            left,
            top,
        };

        if (attachment.classify !== AttachmentClassify.AUDIO) {
            style = {
                ...style,
                width,
                height,
            };
        }

        return {
            attachment: {
                id: attachment.id,
                name: attachment.name,
                classify: attachment.classify,
                size: attachment.size,
                type: attachment.type,
                showType,
            },
            style,
            subType: ShapeSubType.TEXT_ELEMENT,
            [Locks.LOCKS]: { noGrp: 1 }, // 禁止组合
        };
    }

    private getFramePosition(index: number, initLeft: number, initTop: number) {
        const top = initTop + ((DEFAULT_TEXT_HEIGHT + SPACE) * index);
        return {
            top,
            left: initLeft,
        };
    }

    private findAttachmentInitPos(): { initLeft: number; initTop: number } {
        const { slideWidth, slideHeight } = this.uiDataService.presentation.props;
        const curSlide = this.selectionService.currentSlide;
        const locList: AttachmentRect[] = [];
        curSlide?.children.forEach((element) => {
            if (get(element, 'props.attachment') && element.type === UIModelType.SHAPE) {  // 过滤上传没有成功且未在上传中队列中的元素
                const { attachment } = (element as IShapeModel).props;
                if (!attachment.objKey && !this.isUploadingAttachment(attachment.id)) {
                    return;
                }
            }
            if (((element.type === UIModelType.IMAGE && (element as IPictureModel).isMedia?.())
                || element.isAttachment?.()) && get(element, 'props.style')) {
                const { style } = element.props;
                locList.push({ left: style.left, top: style.top, width: style.width, height: style.height  });
            }
        });
        const defaultInitTop = 200;  // 默认top位置
        const defaultInitLeft = slideWidth / 4;  // 默认lef位置在1/4位置处
        const space = SPACE; // 间距
        let initTop = defaultInitTop;
        let initLeft = defaultInitLeft;
        const horCount = Math.ceil((slideWidth - initLeft) / (DEFAULT_TEXT_WIDTH + space));
        const verCount = Math.ceil((slideHeight - initTop) / (DEFAULT_TEXT_HEIGHT + space));
        // eslint-disable-next-line no-restricted-syntax
        outer:
        for (let hi = 0; hi < horCount; hi ++) {
            initLeft = defaultInitLeft + ((DEFAULT_TEXT_WIDTH + space) * hi);
            for (let vi = 0; vi < verCount; vi ++) {
                initTop = defaultInitTop + ((DEFAULT_TEXT_HEIGHT + space) *  vi);
                let intersect = false;
                // eslint-disable-next-line @typescript-eslint/prefer-for-of
                for (let i = 0; i < locList.length; i++) {
                    if (isIntersect(locList[i], { left: initLeft, top: initTop,
                        width: DEFAULT_TEXT_WIDTH, height: DEFAULT_TEXT_HEIGHT })) {
                        intersect = true;
                        break;
                    }
                }
                if (!intersect) {
                    break outer;
                }
            }
        }
        return { initLeft, initTop };
    }

    /**
     * 触发转码
     * @param objKey string
     * @returns Promise<void>
     */
    private async transCode(objKey: string, trancodeType: TranscodeType): Promise<void> {
        if (ENV.IS_LOCAL_EDIT) return;
        try {
            await axios.post('/api/drive/transcode', {
                file_id: SlideDataServices.DocInfoManager()?.padId ?? '',
                obj_key: objKey,
                transcode_type: trancodeType,
            });
        } catch (err) {
            window.log?.error?.('transCode err=', err);
        }
    }

    /**
     * 视频上传成功自动触发的转码
     * @param attachment
     */
    private async autoVideoTransCode(attachment: AttachmentInfo) {
        // 视频上传成功后触发自动转码和上报
        if (isVip()) {
            this.transCode(attachment.objKey!, TranscodeType.MP4ORIGINAL);
            // 上报自动转码, 0 - 480p , 1 - 480p以及原画
            this.reportVideoAttachment(TDWActionType.AUTO_TRANSCODE, '1');
        } else {
            // 上报自动转码, 0 - 480p , 1 - 480以及原画
            this.reportVideoAttachment(TDWActionType.AUTO_TRANSCODE, '0');
        }
        this.transCode(attachment.objKey!, TranscodeType.MP4FLU);
        // 上报插入视频成功
        this.reportVideoAttachment(TDWActionType.INSERT_VIDEO_SUCCESS, attachment.type);
    }

    /**
     * 获取音视频附件时长
     * @param attachment AttachmentInfo
     * @returns Promise<string | null>
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public async getMediaDuration(attachment:
    {objKey: string, classify: IAttachmentClassify}): Promise<string | null> {
        try {
            const mediaInfo = await axios.post(`https://docs.qq.com/api/drive/mediainfo?xsrf=${getXSRF()}`, {
                file_id: SlideDataServices.DocInfoManager()?.padId ?? '',
                obj_key: attachment.objKey,
            });
            const type = attachment.classify as string;
            return mediaInfo.data.result.media[type].length;
        } catch (err) {
            return null;
        }
    }

    /**
     * 添加视频附件元素
     * @param attachment
     * @param attachElement
     * @param index
     * @param initLeft
     * @param initTop
     */
    private async addVideoAttachmentElement(
        attachment: any,
        attachElement: AttachmentElement,
        index: number,
        initLeft: number,
        initTop: number,
    ) {
        const slide = this.selectionService.currentSlide;
        if (!slide) return;

        // 获取视频属性
        const { videoInfo } = attachment;
        // 浏览器解析不了的文件还是走新增 shape ，上传完成后删除shape、新增 media 逻辑
        if (!videoInfo) {
            this.addNormalAttachmentElement(attachment, attachElement, index, initLeft, initTop);
            return;
        }

        // 浏览器解析不了的文件还是走新增 shape ，上传完成后删除shape、新增 media 逻辑
        const { width, height, duration, thumbnailBlob } = videoInfo || {};
        if (!width || !height || !duration || !thumbnailBlob) {
            this.addNormalAttachmentElement(attachment, attachElement, index, initLeft, initTop);
            return;
        }

        !ENV.IS_PC_LOCAL_EDIT && this.showSnackbar({
            message: i18n.t('视频上传中, 当前使用本地文件加速预览'),
            duration: 3000,
            type: 'loading',
        });

        /* 获取缩略图后(不直接使用缩略图，而是)重新保存到 cdn 然后落盘因为附件的这些地址有有效期，会导致导出的时候链接失效 */
        const posterUrl = await blobToBase64(thumbnailBlob);

        const videoSize = getDefaultVideoSizeByRatio(width / (height || 100));
        const videoImage = posterUrl || VIDEO_DEFAULT_IMG;
        // 添加元素
        const props = {
            src: videoImage,
            style: {
                bgShape: GraphType.RECT,
                left: initLeft,
                top: initTop,
                width: videoSize.width,
                height: videoSize.height,
            },
        };
        const extraInfo = {
            video: {
                link: TEMPLINK + attachment.id, // 不传改值会导致没有 video, 这里用 link 暂时存储 attachment id, 以及用 TEMPLINK 表示正在上传
                format: attachment.type,
                size: attachment.size,
            },
            name: attachment.name,
        };
        slide.addElement([UIModelType.IMAGE], [props], [extraInfo]);
        // 添加完元素后，更新shapeId
        const element = slide.children[slide.children.length - 1];
        attachElement.shapeId = element.id as string;
        attachElement.shapeUniqueId = (element.sdkData as BaseModelSdkTypeV3)?.id;
        this.updateModelIdToLocalAttachment(attachElement.shapeId, {
            ...attachment,
            status: EStatusEvents.FILEREAD,
            showType: EShowType.PREVIEW,
        });
    }

    /**
     * (本地秒播)处理视频上传成功后的逻辑: 更新数据
     * @param options
     * @param attachment
     */
    private async handleVideoSuccess(model: IPictureModel | IBaseModel, attachment: AttachmentInfo) {
        if (isShapeModel(model)) {
            // 浏览器解析不了的文件还是走新增 shape ，上传完成后删除shape、新增 media 逻辑
            this.handleMediaSuccess(model, attachment);
        } else if (isVideoElement(model)) {
            this.autoVideoTransCode(attachment);
            // 1.不用设置宽高, addVideoAttachmentElement 时已设置
            const localAttachment = this.getLocalAttachment(model.id as string);
            const mediaDuration = localAttachment?.videoInfo?.duration || 0;

            // 2.封面图片落保存到 cdn 然后落盘 (因为附件的这些地址有有效期，会导致导出的时候链接失效)
            const posterBlob = localAttachment?.videoInfo?.thumbnailBlob;
            let videoImage;
            try {
                const res = (await uploadImageBlob({ blob: posterBlob })) ?? {};
                videoImage = res?.url;
            } catch (err) {
                console.error('===uploadImageBlob err:', err);
            }

            // 清空本地 attachment 的 videoInfo，避免过多引用, 留下 url 用于 dc 组件做判断
            // this.updateModelIdToLocalAttachment(model.id as string, {
            //     videoInfo: { url: localAttachment?.videoInfo?.url },
            // });

            !ENV.IS_PC_LOCAL_EDIT && this.showSnackbar({
                message: i18n.t('视频上传完成'),
                duration: 3000,
                type: 'success',
            });

            // 3.不添加动画，之前 addVideoAttachmentElement 时已设置
            transaction(async () => {
                const slide = this.selectionService.currentSlide;
                if (!slide) return;

                // 4. 更新封面图片、 media 等属性
                const props = { imageType: UPLOAD_VIDEO_FILE_POSTER_IMAGE_TYPE, media: { embed: attachment.objKey } };
                if (videoImage) {
                    Object.assign(props, { src: videoImage });
                }
                model.updateProps(props);

                // 5. 更新 video link，上传完成前为 TEMPLINK + 'xxx'，以此来判断视频是否正在上传
                // 这里 link 更新后会中断视频播放
                model.updateMediaProps({
                    link: attachment.objKey,
                    format: attachment.type,
                    size: attachment.size,
                });
                if (attachment.objKey) {
                    const fileDescriptor = {
                        id: attachment.objKey,
                        src: attachment.objKey,
                        format: attachment.type,
                        size: attachment.size,
                    };

                    this.addAnimationToVideo(slide, `${mediaDuration * 1000}`);

                    SlideDataServices.SDKDataServices.PresentationStoreService()
                        ?.getPresentation().api.updateVideoResourceMap(attachment.objKey, fileDescriptor);
                }
            });
        }
    }

    /**
     * 处理音视频上传成功后的逻辑：删除文本占位，添加图片占位
     * @param shapeModel
     * @param attachment
     */
    private async handleMediaSuccess(shapeModel: IBaseModel, attachment: AttachmentInfo) {
        const { classify } = attachment;
        const isVideo = classify === AttachmentClassify.VIDEO;
        const isAudio = classify === AttachmentClassify.AUDIO;
        const slide = shapeModel.slideRef;
        if (!slide) return;
        if (isVideo) this.autoVideoTransCode(attachment);
        // 设置封面,视频宽高
        const videoUrl = attachment.objKey ?? '';
        let videoImage;
        let videoSize;
        if (isVideo) {
            if (ENV.IS_PC_LOCAL_EDIT) {
                videoImage = MediaUtils.VideoImageMap.get(videoUrl) ?? VIDEO_DEFAULT_IMG;
                videoSize = MediaUtils.VideoSizeMap.get(videoUrl)
                    ?? { width: DEFAULT_VIDEO_WIDTH, height: DEFAULT_VIDEO_HEIGHT };
            } else {
                const { url: posterUrl, width: posterWidth, height: posterHeight } = (
                    await this.uploadAndGetMediaPosterUrl(attachment) || {}
                );
                let aspectRatio;
                if (posterWidth && posterHeight) {
                    aspectRatio = posterWidth / posterHeight;
                }
                videoSize = getDefaultVideoSizeByRatio(aspectRatio);
                videoImage = posterUrl || VIDEO_DEFAULT_IMG;
            }
        }
        // 删除此元素，添加音视频元素
        const { left, top } = get(shapeModel, 'props.style');
        const extraInfo = classify ? {
            [classify]: {
                link: attachment.objKey,
                format: attachment.type,
                size: attachment.size,
            },
            name: attachment.name,
        } : {};
        const src = isVideo ? videoImage : AUDIO_DEFAULT_IMG;
        // 本地编辑场景需下载封面占位图到本地，并更新 imageResourceMap
        const needLocalImage = ENV.IS_LOCAL_EDIT && src?.startsWith('http');
        const newSrc = needLocalImage ? (await replaceMediaUrl(src)).src : src;
        const props = {
            src: newSrc,
            style: {
                bgShape: GraphType.RECT,
                left: left || DEFAULT_POS,
                top: top || DEFAULT_POS,
                width: isVideo ? videoSize.width : DEFAULT_AUDIO_WIDTH,
                height: isVideo ? videoSize.height : DEFAULT_AUDIO_HEIGHT,
            },
            media: { embed: attachment.objKey },
        };
        // 获取音视频时长
        const mediaDuration = await this.handleGetMediaDuration(attachment);
        transaction(() => {
            slide.deleteElement([shapeModel]);
            slide.addElement([UIModelType.IMAGE], [props], [extraInfo]);
            if (isVideo) {
                // 通过addAnimation设置触发器动画
                this.addAnimationToVideo(slide, mediaDuration);
            } else if (isAudio) {
                this.addAnimationToAudio(slide, mediaDuration);
            }
            if (attachment.objKey) {
                const fileDescriptor = {
                    id: attachment.objKey,
                    src: attachment.objKey,
                    format: attachment.type,
                    size: attachment.size,
                };
                if (needLocalImage) {
                    SlideDataServices.SDKDataServices.PresentationStoreService()
                        ?.getPresentation().api.updateImageResourceMap(newSrc, { src: newSrc });
                }
                if (isVideo) {
                    SlideDataServices.SDKDataServices.PresentationStoreService()
                        ?.getPresentation().api.updateVideoResourceMap(attachment.objKey, fileDescriptor);
                } else if (isAudio) {
                    SlideDataServices.SDKDataServices.PresentationStoreService()
                        ?.getPresentation().api.updateAudioResourceMap(attachment.objKey, fileDescriptor);
                }
            }
        });
        // 移除原来的UI
        this.uploader.removeAttachment(attachment.id!);
    }
    /**
     * 获取音视频附件的时长
     * @param attachment
     * @returns mediaDuration
     */
    private async handleGetMediaDuration(attachment: AttachmentInfo) {
        // 判断是否是音视频附件
        const isMedia = attachment.classify === AttachmentClassify.VIDEO
            || attachment.classify === AttachmentClassify.AUDIO;
        if (!isMedia) return NO_MEDIA_DURATION;
        const { objKey, classify } = attachment;
        if (!objKey || !classify) return NO_MEDIA_DURATION;
        const mediaDuration = await this.getMediaDuration({ objKey, classify });
        // 返回媒体时长并转换为毫秒
        return mediaDuration ? `${mediaDuration}000` : NO_MEDIA_DURATION;
    }

    /**
     * 视频附件添加播放和暂停动画
     * @param slide
     * @param mediaDuration
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public addAnimationToVideo(slide: ISlideModel, mediaDuration: string) {
        this.handleAddAnimation(slide, mediaDuration, ResourceType.Video);
    }

    /**
     * 音频附件添加播放动画
     * @param slide
     * @param mediaDuration
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public addAnimationToAudio(slide: ISlideModel, mediaDuration: string) {
        this.handleAddAnimation(slide, mediaDuration, ResourceType.Audio);
    }

    /**
     * 音视频频附件添加控制动画实现
     * @param slide
     * @param mediaDuration
     * @param mediaType
     * @returns
     */
    private handleAddAnimation(slide: ISlideModel, mediaDuration: string, mediaType: ResourceType) {
        const shapeList = slide.sdkData?.shapeList;
        if (!shapeList) return;
        const pictureModel = shapeList[shapeList.length - 1];
        const triggerId = pictureModel.id;
        const mainSeqLength = slide.timing.unitTimeNodeMap[K_DEFAULT_TRIGGER_ID]
            ? slide.timing.unitTimeNodeMap[K_DEFAULT_TRIGGER_ID].unitTimeNodeList.length
            : 0;
        const addPlayAnimationParam = {
            mediaDuration,
            shapeIds: [triggerId],
            animType: AnimType.Play,
            triggerId: K_DEFAULT_TRIGGER_ID,
            index: mainSeqLength,
            mediaType,
        };
        slide.sdkData?.timing?.api.addAnimation(addPlayAnimationParam);
        if (mediaType === ResourceType.Video && triggerId) {
            const interactionSeqLength = slide.timing.unitTimeNodeMap[triggerId]
                ? slide.timing.unitTimeNodeMap[triggerId].unitTimeNodeList.length
                : 0;
            const addPauseAnimationParam = {
                mediaDuration,
                triggerId,
                shapeIds: [triggerId],
                animType: AnimType.Pause,
                index: interactionSeqLength,
                mediaType,
            };
            slide.sdkData?.timing?.api.addAnimation(addPauseAnimationParam);
        }
    }

    /**
     * 处理非音视频上传成功后的逻辑：更新文本占位数据
     * @param shapeModel
     * @param attachment
     */
    private handleAttachmentSuccess(shapeModel: IBaseModel, attachment: AttachmentInfo) {
        // 更新shape attachment数据信息
        const props = {
            attachment: {
                objKey: attachment.objKey,
                id: '',
                showType: EShowType.SIMPLE_TEXT,
            },
            [Locks.LOCKS]: null,  // 上传完成后可以组合
        };
        shapeModel.updateProps(props);
        // 移除原来的UI
        this.uploader.removeAttachment(attachment.id!);
    }

    /**
     * 处理图片附件上传成功后的逻辑：更新文本占位数据
     * @param shapeModel
     * @param attachment
     */
    private async handleImageSuccess(shapeModel: IBaseModel, attachment: AttachmentInfo) {
        const slide = shapeModel.slideRef;
        if (!slide) return;
        const {
            url: src, width: posterWidth, height: posterHeight,
        } = await this.getImageAttachmentPoster(attachment) || {};
        let aspectRatio;
        if (posterWidth && posterHeight) {
            aspectRatio = posterWidth / posterHeight;
        }
        const { width, height } = getDefaultImageSizeByRatio(aspectRatio);
        transaction(() => {
            const { left, top } = get(shapeModel, 'props.style');
            const props = {
                src,
                attachment: {
                    id: '',
                    name: attachment.name,
                    objKey: attachment.objKey,
                    type: attachment.type,
                    size: attachment.size,
                    classify: attachment.classify,
                    showType: EShowType.PREVIEW,
                },
                style: {
                    bgShape: GraphType.RECT,
                    left: left || DEFAULT_POS,
                    top: top || DEFAULT_POS,
                    width,
                    height,
                },
            };
            slide.deleteElement([shapeModel]);
            slide.addElement([UIModelType.IMAGE], [props], [{}]);
            if (attachment.objKey) {
                const fileDescriptor = {
                    id: attachment.objKey,
                    src: attachment.objKey,
                    format: attachment.type,
                    size: attachment.size,
                };
                SlideDataServices.SDKDataServices.PresentationStoreService()
                    ?.getPresentation().api.updateImageResourceMap(attachment.objKey, fileDescriptor);
            }
        });

        this.uploader.removeAttachment(attachment.id!);
    }

    private handleDocSuccess(shapeModel: IBaseModel, attachment: AttachmentInfo) {
        // 更新shape attachment数据信息
        const props = {
            attachment: {
                objKey: attachment.objKey,
                id: '',
                showType: EShowType.CARD,
            },
            [Locks.LOCKS]: null,  // 上传完成后可以组合
            // 文档卡片类型更像形状而不是文本框
            subType: ShapeSubType.SHAPE,
        };
        shapeModel.updateProps(props);
        // 移除原来的UI
        this.uploader.removeAttachment(attachment.id!);
    }

    private showSnackbar(options: TSnackbarShowConfig) {
        this.snackbar?.close();
        this.snackbar = Snackbar.show(options);
    }

    private reportVideoAttachment(action: string, ver5?: string) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.VIDEO_ATTACHMENT,
            action,
            ver5,
        });
    }
}
